import React from 'react';
import { Shield, Package, Handshake, ExternalLink } from 'lucide-react';

/**
 * Resolve how the Safety / Material / Collaborator info cards should render
 * for a given product. Flattened logic replaces nested ternaries.
 */
const resolveQuickInfo = (product) => {
  const hideSafety =
    product.category === 'Wear Resistance' || product.category === 'Dust Suppression System';
  const isWear = product.category === 'Wear Resistance';
  const isDustProduct = product.category === 'Dust Suppression System';
  const showInlineCollab = isDustProduct && product.collaborator;

  let gridCols;
  if (!hideSafety) {
    gridCols = 'grid-cols-2';
  } else if (showInlineCollab) {
    gridCols = 'grid-cols-1 md:grid-cols-2';
  } else {
    gridCols = 'grid-cols-1';
  }

  const wrapperClass = isWear ? 'max-w-xs' : '';

  let materialLabel;
  if (product.material) {
    materialLabel = product.material;
  } else if (isWear) {
    materialLabel = 'Ceramic';
  } else {
    materialLabel = 'ISI Standard Steel';
  }

  return { hideSafety, showInlineCollab, gridCols, wrapperClass, materialLabel };
};

const ProductQuickInfo = ({ product }) => {
  const { hideSafety, showInlineCollab, gridCols, wrapperClass, materialLabel } = resolveQuickInfo(product);

  return (
    <div className={`grid ${gridCols} gap-4 mb-8 ${wrapperClass}`}>
      {!hideSafety && (
        <div className="group relative">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-6 hover:border-cyan-300 transition-all hover:shadow-xl">
            <Shield className="h-8 w-8 text-cyan-600 mb-3" />
            <div className="text-sm text-gray-600">Safety Standard</div>
            <div className="text-gray-900 font-semibold">ISO India Compliant</div>
          </div>
        </div>
      )}
      <div className="group relative">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-6 hover:border-cyan-300 transition-all hover:shadow-xl h-full">
          <Package className="h-8 w-8 text-cyan-600 mb-3" />
          <div className="text-sm text-gray-600">Material</div>
          <div className="text-gray-900 font-semibold">{materialLabel}</div>
        </div>
      </div>
      {showInlineCollab && (
        <div className="group relative" data-testid="product-collaborator">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative bg-white/90 backdrop-blur-xl border border-gray-200 rounded-xl p-6 hover:border-cyan-300 transition-all hover:shadow-xl h-full flex flex-col justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                <Handshake className="h-5 w-5 text-white" />
              </div>
              <div className="text-sm font-semibold text-gray-700">Our Collaborator</div>
            </div>
            <a
              href={product.collaborator.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg px-4 py-2.5 font-semibold text-sm hover:shadow-lg hover:shadow-cyan-500/30 transform hover:scale-105 transition-all w-full"
              data-testid="collaborator-link"
            >
              <span>{product.collaborator.name}</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductQuickInfo;
